const S = {
  wrapper:      { marginTop:'10px', padding:'40px', border:'3px solid grey', fontFamily:"'Georgia', serif", color:'#1a1a2e', lineHeight:'1.7' },
  h1:           { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #2E75B6', paddingBottom:'8px', marginBottom:'24px', color:'#1F3864' },
  h2:           { fontSize:'1.3rem', fontWeight:'700', color:'#2E75B6', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #2E75B6', paddingLeft:'10px' },
  p:            { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox:      { background:'#EBF3FB', border:'2px solid #2E75B6', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New', monospace", fontSize:'0.88rem', color:'#1F3864', marginBottom:'8px' },
  infoBoxItem:  { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #2E75B6' },
  table:        { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th:           { background:'#2E75B6', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE:          { padding:'10px 14px', borderBottom:'1px solid #ccd9ea', verticalAlign:'top', background:'#f5f9ff' },
  tdO:          { padding:'10px 14px', borderBottom:'1px solid #ccd9ea', verticalAlign:'top', background:'#fff' },
  tdL:          { fontWeight:'600', color:'#1F3864', whiteSpace:'nowrap', width:'220px' },
  code:         { fontFamily:"'Courier New', monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap:        { margin:'24px 0', background:'#f8fbff', border:'1px solid #ccd9ea', borderRadius:'8px', padding:'20px' },
  dTitle:       { fontWeight:'700', fontSize:'0.95rem', color:'#1F3864', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption:     { fontSize:'0.84rem', color:'#555', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width:'220px' }}>Concept</th>
        <th style={S.th}>Formula / Notes</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Diagram 1: Tensor Rank Hierarchy ────────────────────────────
function DiagramRankHierarchy() {
  const W = 900, H = 340;

  // Four panels: rank 0, 1, 2, 3
  const items = [
    {
      rank: 'Rank 0', name: 'Scalar', symbol: 'a',
      color: '#2E75B6', bg: '#EBF3FB', bc: '#2E75B6',
      indices: '(no indices)',
      example: 'Temperature T = 23.5',
      components: '1 component',
    },
    {
      rank: 'Rank 1', name: 'Vector', symbol: 'vⁱ or vᵢ',
      color: '#27ae60', bg: '#f0fff4', bc: '#27ae60',
      indices: '1 index',
      example: 'Force F = [Fx, Fy, Fz]',
      components: 'n components',
    },
    {
      rank: 'Rank 2', name: 'Matrix / 2-Tensor', symbol: 'Tⁱʲ',
      color: '#e07b00', bg: '#fff7ee', bc: '#e07b00',
      indices: '2 indices',
      example: 'Stress tensor σᵢⱼ',
      components: 'n² components',
    },
    {
      rank: 'Rank 3', name: '3-Tensor', symbol: 'Tⁱʲᵏ',
      color: '#9b59b6', bg: '#f5d0ff', bc: '#9b59b6',
      indices: '3 indices',
      example: 'Piezoelectric tensor dᵢⱼₖ',
      components: 'n³ components',
    },
  ];

  const PW = (W - 56) / 4;
  const p = i => 14 + i * (PW + 14);
  const VISUAL_Y = 60;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Tensor Rank Hierarchy — Scalars, Vectors, Matrices, and Higher-Order Tensors</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {items.map(({ rank, name, symbol, color, bg, bc, indices, example, components }, i) => {
          const cx = p(i) + PW / 2;

          return (
            <g key={i}>
              <rect x={p(i)} y={10} width={PW} height={H - 20} rx={5} fill="#fff" stroke="#ccd9ea"/>

              {/* Header */}
              <rect x={p(i)} y={10} width={PW} height={34} rx={5} fill={color}/>
              <rect x={p(i)} y={34} width={PW} height={10} fill={color}/>
              <text x={cx} y={32} textAnchor="middle" fontSize="13" fill="#fff" fontWeight="700">{rank}: {name}</text>

              {/* Visual */}
              {i === 0 && (
                // Scalar: single dot
                <circle cx={cx} cy={VISUAL_Y + 50} r={18} fill={color} fillOpacity="0.8"/>
              )}
              {i === 1 && (
                // Vector: arrow
                <g>
                  <line x1={cx - 38} y1={VISUAL_Y + 50} x2={cx + 30} y2={VISUAL_Y + 50} stroke={color} strokeWidth="3"/>
                  <polygon points={`${cx+38},${VISUAL_Y+50} ${cx+26},${VISUAL_Y+44} ${cx+26},${VISUAL_Y+56}`} fill={color}/>
                  {[0,1,2].map(k => (
                    <rect key={k} x={cx - 38 + k * 24} y={VISUAL_Y + 43} width={18} height={14} rx={2}
                      fill={color} fillOpacity={0.25 + k * 0.2} stroke={color} strokeWidth="0.8"/>
                  ))}
                </g>
              )}
              {i === 2 && (
                // Matrix: 3x3 grid
                <g>
                  {[0,1,2].map(row => [0,1,2].map(col => (
                    <rect key={`${row}-${col}`}
                      x={cx - 33 + col * 23} y={VISUAL_Y + 30 + row * 23}
                      width={20} height={20} rx={2}
                      fill={color} fillOpacity={0.15 + (row + col) * 0.08}
                      stroke={color} strokeWidth="1"/>
                  )))}
                </g>
              )}
              {i === 3 && (
                // 3-tensor: stacked grids (perspective)
                <g>
                  {[0,1,2].map(layer => (
                    <g key={layer}>
                      {[0,1,2].map(row => [0,1,2].map(col => (
                        <rect key={`${layer}-${row}-${col}`}
                          x={cx - 32 + col * 18 + layer * 7}
                          y={VISUAL_Y + 22 + row * 18 - layer * 7}
                          width={15} height={15} rx={1}
                          fill={color} fillOpacity={0.1 + layer * 0.12}
                          stroke={color} strokeWidth="0.8"/>
                      )))}
                    </g>
                  ))}
                </g>
              )}

              {/* Index notation */}
              <rect x={p(i)+10} y={VISUAL_Y+110} width={PW-20} height={22} rx={3} fill={bg} stroke={bc} strokeWidth="1"/>
              <text x={cx} y={VISUAL_Y+125} textAnchor="middle" fontSize="13" fill={color} fontFamily="'Courier New',monospace" fontWeight="700">{symbol}</text>

              {/* Info rows */}
              {[
                { label:'Indices:', val: indices },
                { label:'Example:', val: example },
                { label:'Size:', val: components },
              ].map(({ label, val }, j) => (
                <g key={j}>
                  <text x={p(i)+14} y={VISUAL_Y + 154 + j * 42} fontSize="11" fill="#888" fontWeight="700">{label}</text>
                  <text x={p(i)+14} y={VISUAL_Y + 169 + j * 42} fontSize="11" fill="#333" fontFamily="'Courier New',monospace">{val}</text>
                </g>
              ))}
            </g>
          );
        })}

        {/* Arrows connecting panels */}
        {[0,1,2].map(i => {
          const x = p(i) + PW + 7;
          return (
            <g key={i}>
              <polygon points={`${x+1},${H/2} ${x-5},${H/2-5} ${x-5},${H/2+5}`} fill="#bbb"/>
            </g>
          );
        })}

      </svg>
      <div style={S.dCaption}>A tensor of rank r in n dimensions has n^r components and r indices. Rank-0 tensors are scalars; rank-1 are vectors; rank-2 generalise matrices. Each rank adds a dimension of indexing. In 3D: a scalar has 1 component, a vector 3, a matrix 9, a rank-3 tensor 27.</div>
    </div>
  );
}

// ── Diagram 2: Index Notation & Einstein Summation ───────────────
function DiagramEinstein() {
  const W = 900, H = 370;
  const LP = 14, LW = 420;
  const RP = LP + LW + 18, RW = W - RP - 14;

  // Left: annotated Einstein summation examples
  const examples = [
    {
      raw:   'c = aⁱ bᵢ',
      desc:  'Dot product (inner product)',
      expd:  'c = a¹b₁ + a²b₂ + a³b₃',
      color: '#2E75B6', bg: '#EBF3FB', bc: '#2E75B6',
    },
    {
      raw:   'Cⁱⱼ = AⁱₖBᵏⱼ',
      desc:  'Matrix multiplication',
      expd:  'Cⁱⱼ = Σₖ AⁱₖBᵏⱼ  (k summed)',
      color: '#27ae60', bg: '#f0fff4', bc: '#27ae60',
    },
    {
      raw:   'Tⁱᵢ  (trace)',
      desc:  'Contraction over one index pair',
      expd:  'T¹₁ + T²₂ + T³₃ = tr(T)',
      color: '#e07b00', bg: '#fff7ee', bc: '#e07b00',
    },
    {
      raw:   'vⁱ = Tⁱⱼ uʲ',
      desc:  'Tensor acting on a vector',
      expd:  'vⁱ = Σⱼ Tⁱⱼ uʲ  (matrix-vector)',
      color: '#9b59b6', bg: '#f5d0ff', bc: '#9b59b6',
    },
    {
      raw:   'Sᵢⱼₖₗ = AᵢⱼBₖₗ',
      desc:  'Outer (tensor) product',
      expd:  'No repeated index → no sum, rank increases',
      color: '#c0392b', bg: '#fff0f0', bc: '#c0392b',
    },
  ];

  // Right: rules panel
  const rules = [
    { title: 'Free index', note: 'Appears exactly once — labels a component.\nMust match on both sides of an equation.' },
    { title: 'Dummy (repeated) index', note: 'Appears exactly twice (once up, once down).\nImplies summation — rename freely.' },
    { title: 'Up index (contravariant)', note: 'Written as superscript: vⁱ\nTransforms with inverse of change-of-basis.' },
    { title: 'Down index (covariant)', note: 'Written as subscript: wᵢ\nTransforms with the change-of-basis matrix.' },
  ];

  const rowH = 58, hdr = 36;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Index Notation and Einstein Summation Convention</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* Left panel */}
        <rect x={LP} y={10} width={LW} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={LP+LW/2} y={30} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">Summation Convention Examples</text>
        <text x={LP+LW/2} y={44} textAnchor="middle" fontSize="11" fill="#555">Repeated index (one up, one down) implies sum over 1..n</text>

        {examples.map(({ raw, desc, expd, color, bg, bc }, i) => {
          const y = hdr + 8 + i * (rowH + 4);
          return (
            <g key={i}>
              <rect x={LP+10} y={y} width={LW-20} height={rowH} rx={3} fill={bg} stroke={bc} strokeWidth="1"/>
              <text x={LP+20} y={y+18} fontSize="14" fill={color} fontFamily="'Courier New',monospace" fontWeight="700">{raw}</text>
              <text x={LP+20} y={y+34} fontSize="11" fill="#1F3864" fontWeight="700">{desc}</text>
              <text x={LP+20} y={y+50} fontSize="11" fill="#444" fontFamily="'Courier New',monospace">{expd}</text>
            </g>
          );
        })}

        {/* Right panel */}
        <rect x={RP} y={10} width={RW} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={RP+RW/2} y={30} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">Index Rules</text>

        {/* Mnemonic box */}
        <rect x={RP+10} y={36} width={RW-20} height={38} rx={3} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1.5"/>
        <text x={RP+RW/2} y={52} textAnchor="middle" fontSize="12" fill="#1F3864" fontWeight="700">Each index appears at most twice.</text>
        <text x={RP+RW/2} y={66} textAnchor="middle" fontSize="11" fill="#555">Once = free.  Twice (↑↓) = sum.</text>

        {rules.map(({ title, note }, i) => {
          const y = 84 + i * 70;
          const colors = ['#2E75B6','#27ae60','#e07b00','#9b59b6'];
          const bgs    = ['#EBF3FB','#f0fff4','#fff7ee','#f5d0ff'];
          const bcs    = ['#2E75B6','#27ae60','#e07b00','#9b59b6'];
          return (
            <g key={i}>
              <rect x={RP+10} y={y} width={RW-20} height={62} rx={3} fill={bgs[i]} stroke={bcs[i]} strokeWidth="1"/>
              <text x={RP+20} y={y+17} fontSize="12" fill={colors[i]} fontWeight="700">{title}</text>
              {note.split('\n').map((ln, j) => (
                <text key={j} x={RP+20} y={y+33+j*16} fontSize="11" fill="#444">{ln}</text>
              ))}
            </g>
          );
        })}

      </svg>
      <div style={S.dCaption}>Einstein summation eliminates explicit sigma signs: any index appearing twice (once up, once down) is summed over all values. Free indices label tensor components and must appear identically on both sides. This compact notation is the universal language of tensor algebra and differential geometry.</div>
    </div>
  );
}

// ── Diagram 3: Covariant vs Contravariant & the Metric ───────────
function DiagramMetric() {
  const W = 900, H = 360;
  const PW = (W - 42) / 2;
  const pL = 14, pR = pL + PW + 14;

  // Left panel: oblique basis illustration
  // Basis vectors e1, e2 at an angle
  const ox = pL + 70, oy = 240;
  const e1 = [120, -30];  // e1 direction
  const e2 = [40, -110];  // e2 direction (non-orthogonal)

  function addV(a, b) { return [a[0]+b[0], a[1]+b[1]]; }
  function scaleV(s, v) { return [s*v[0], s*v[1]]; }

  // Vector v = 1.3 e1 + 0.9 e2
  const c1 = 1.3, c2 = 0.9;
  const vTip = addV(scaleV(c1, e1), scaleV(c2, e2));

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Covariant and Contravariant Components — Oblique Basis and the Metric Tensor</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* Left panel: basis diagram */}
        <rect x={pL} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={pL+PW/2} y={28} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">Oblique Basis: Contravariant vs Covariant</text>

        {/* Grid lines along e1 */}
        {[-1,0,1,2].map(k => (
          <line key={`g1-${k}`}
            x1={ox + k*e1[0] + (-1)*e2[0]} y1={oy + k*e1[1] + (-1)*e2[1]}
            x2={ox + k*e1[0] +   2*e2[0]} y2={oy + k*e1[1] +   2*e2[1]}
            stroke="#eee" strokeWidth="1"/>
        ))}
        {/* Grid lines along e2 */}
        {[-1,0,1,2].map(k => (
          <line key={`g2-${k}`}
            x1={ox + (-1)*e1[0] + k*e2[0]} y1={oy + (-1)*e1[1] + k*e2[1]}
            x2={ox +   2*e1[0] + k*e2[0]} y2={oy +   2*e1[1] + k*e2[1]}
            stroke="#eee" strokeWidth="1"/>
        ))}

        {/* Basis vectors */}
        <line x1={ox} y1={oy} x2={ox+e1[0]} y2={oy+e1[1]} stroke="#2E75B6" strokeWidth="2.5"/>
        <polygon points={`${ox+e1[0]},${oy+e1[1]} ${ox+e1[0]-9*0.97},${oy+e1[1]+9*0.25} ${ox+e1[0]-9*(-0.25)},${oy+e1[1]+9*0.97}`} fill="#2E75B6"/>
        <text x={ox+e1[0]+6} y={oy+e1[1]+4} fontSize="14" fill="#2E75B6" fontWeight="700">e₁</text>

        <line x1={ox} y1={oy} x2={ox+e2[0]} y2={oy+e2[1]} stroke="#27ae60" strokeWidth="2.5"/>
        <polygon points={`${ox+e2[0]},${oy+e2[1]} ${ox+e2[0]+9*0.34},${oy+e2[1]+9*0.94} ${ox+e2[0]+9*0.94},${oy+e2[1]-9*0.34}`} fill="#27ae60"/>
        <text x={ox+e2[0]+6} y={oy+e2[1]-4} fontSize="14" fill="#27ae60" fontWeight="700">e₂</text>

        {/* Vector v */}
        <line x1={ox} y1={oy} x2={ox+vTip[0]} y2={oy+vTip[1]} stroke="#c0392b" strokeWidth="2.5"/>
        <polygon points={`${ox+vTip[0]},${oy+vTip[1]} ${ox+vTip[0]-8},${oy+vTip[1]+5} ${ox+vTip[0]-5},${oy+vTip[1]-8}`} fill="#c0392b"/>
        <text x={ox+vTip[0]+6} y={oy+vTip[1]} fontSize="14" fill="#c0392b" fontWeight="700">v</text>

        {/* Contravariant projections (along grid lines) */}
        <line x1={ox+vTip[0]} y1={oy+vTip[1]}
              x2={ox+c1*e1[0]} y2={oy+c1*e1[1]}
              stroke="#27ae60" strokeWidth="1.3" strokeDasharray="5,3"/>
        <line x1={ox+vTip[0]} y1={oy+vTip[1]}
              x2={ox+c2*e2[0]} y2={oy+c2*e2[1]}
              stroke="#2E75B6" strokeWidth="1.3" strokeDasharray="5,3"/>

        {/* Labels for contravariant components */}
        <text x={ox+c1*e1[0]/2-10} y={oy+c1*e1[1]/2+16} fontSize="12" fill="#2E75B6" fontWeight="700">v¹=1.3</text>
        <text x={ox+c2*e2[0]/2-38} y={oy+c2*e2[1]/2+4}  fontSize="12" fill="#27ae60" fontWeight="700">v²=0.9</text>

        {/* Origin dot */}
        <circle cx={ox} cy={oy} r={4} fill="#1F3864"/>
        <text x={ox-14} y={oy+14} fontSize="11" fill="#888">O</text>

        {/* Legend box */}
        <rect x={pL+10} y={H-72} width={PW-20} height={58} rx={3} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1"/>
        <text x={pL+20} y={H-57} fontSize="11" fill="#1F3864" fontWeight="700">Contravariant vⁱ: components along basis grid lines</text>
        <text x={pL+20} y={H-42} fontSize="11" fill="#555">Covariant vᵢ = gᵢⱼ vʲ: components perpendicular projections</text>
        <text x={pL+20} y={H-27} fontSize="11" fill="#555">In orthonormal bases vⁱ = vᵢ; differ in oblique bases</text>

        {/* Right panel: metric tensor */}
        <rect x={pR} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={pR+PW/2} y={28} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">The Metric Tensor gᵢⱼ</text>

        {/* Definition box */}
        <rect x={pR+10} y={36} width={PW-20} height={44} rx={3} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1.5"/>
        <text x={pR+PW/2} y={53} textAnchor="middle" fontSize="12" fill="#1F3864" fontWeight="700" fontFamily="'Courier New',monospace">gᵢⱼ = eᵢ · eⱼ</text>
        <text x={pR+PW/2} y={69} textAnchor="middle" fontSize="11" fill="#555">Inner products of basis vectors</text>

        {/* What gij does */}
        {[
          { y: 90,  bg:'#EBF3FB', bc:'#2E75B6', color:'#2E75B6', title:'Lower an index (covariant)', formula:'vᵢ = gᵢⱼ vʲ' },
          { y: 148, bg:'#f0fff4', bc:'#27ae60', color:'#27ae60', title:'Raise an index (contravariant)', formula:'vⁱ = gⁱʲ vⱼ  (gⁱʲ = inverse metric)' },
          { y: 206, bg:'#fff7ee', bc:'#e07b00', color:'#e07b00', title:'Compute lengths & angles', formula:'|v|² = gᵢⱼ vⁱ vʲ,  cos θ = gᵢⱼ vⁱ wʲ/(|v||w|)' },
          { y: 264, bg:'#f5d0ff', bc:'#9b59b6', color:'#9b59b6', title:'Euclidean metric (Cartesian)', formula:'gᵢⱼ = δᵢⱼ  (identity matrix)\nRaising/lowering does nothing' },
        ].map(({ y, bg, bc, color, title, formula }) => (
          <g key={y}>
            <rect x={pR+10} y={y} width={PW-20} height={50} rx={3} fill={bg} stroke={bc} strokeWidth="1"/>
            <text x={pR+20} y={y+17} fontSize="12" fill={color} fontWeight="700">{title}</text>
            {formula.split('\n').map((ln, j) => (
              <text key={j} x={pR+20} y={y+33+j*14} fontSize="11" fill="#333" fontFamily="'Courier New',monospace">{ln}</text>
            ))}
          </g>
        ))}

      </svg>
      <div style={S.dCaption}>In a non-orthogonal (oblique) basis, contravariant components vⁱ are read along the coordinate grid lines, while covariant components vᵢ are perpendicular projections. The metric tensor gᵢⱼ = eᵢ·eⱼ converts between them and encodes all information about lengths, angles, and areas in the basis.</div>
    </div>
  );
}

// ── Diagram 4: Tensor Operations ────────────────────────────────
function DiagramOperations() {
  const W = 900, H = 380;
  const PW = (W - 56) / 3;
  const p = i => 14 + i * (PW + 14);

  // Panel 1: Outer product A⊗B
  // Panel 2: Contraction
  // Panel 3: Symmetry / antisymmetry

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Core Tensor Operations — Outer Product, Contraction, and Symmetry</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* ── Panel 1: Outer product ── */}
        <rect x={p(0)} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={p(0)+PW/2} y={28} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">Outer (Tensor) Product</text>
        <text x={p(0)+PW/2} y={43} textAnchor="middle" fontSize="12" fill="#2E75B6" fontFamily="'Courier New',monospace" fontWeight="700">Cⁱʲ = aⁱ ⊗ bʲ</text>

        {/* Column vector a */}
        {['a¹','a²','a³'].map((lbl, row) => (
          <g key={row}>
            <rect x={p(0)+18} y={70+row*30} width={36} height={26} rx={2} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1"/>
            <text x={p(0)+36} y={87+row*30} textAnchor="middle" fontSize="12" fill="#2E75B6" fontFamily="'Courier New',monospace" fontWeight="700">{lbl}</text>
          </g>
        ))}
        <text x={p(0)+36} y={63} textAnchor="middle" fontSize="12" fill="#2E75B6" fontWeight="700">a</text>

        {/* Row vector b */}
        {['b¹','b²','b³'].map((lbl, col) => (
          <g key={col}>
            <rect x={p(0)+80+col*38} y={70} width={34} height={26} rx={2} fill="#f0fff4" stroke="#27ae60" strokeWidth="1"/>
            <text x={p(0)+97+col*38} y={87} textAnchor="middle" fontSize="12" fill="#27ae60" fontFamily="'Courier New',monospace" fontWeight="700">{lbl}</text>
          </g>
        ))}
        <text x={p(0)+97+38} y={63} textAnchor="middle" fontSize="12" fill="#27ae60" fontWeight="700">b</text>

        {/* Result matrix */}
        {[0,1,2].map(row => [0,1,2].map(col => {
          const labels = [['a¹b¹','a¹b²','a¹b³'],['a²b¹','a²b²','a²b³'],['a³b¹','a³b²','a³b³']];
          const alpha = 0.15 + (row+col)*0.1;
          return (
            <g key={`${row}-${col}`}>
              <rect x={p(0)+80+col*38} y={100+row*30} width={34} height={26} rx={2}
                fill="#fff7ee" fillOpacity={0.5+alpha} stroke="#e07b00" strokeWidth="0.8"/>
              <text x={p(0)+97+col*38} y={117+row*30} textAnchor="middle"
                fontSize="8.5" fill="#e07b00" fontFamily="'Courier New',monospace">{labels[row][col]}</text>
            </g>
          );
        }))}
        <text x={p(0)+97+38} y={95} textAnchor="middle" fontSize="11" fill="#e07b00" fontWeight="700">= C (3×3)</text>

        {/* Key facts */}
        <rect x={p(0)+10} y={200} width={PW-20} height={80} rx={3} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1"/>
        <text x={p(0)+18} y={216} fontSize="11" fill="#1F3864" fontWeight="700">Rank: rank(a)+rank(b)</text>
        <text x={p(0)+18} y={232} fontSize="11" fill="#444" fontFamily="'Courier New',monospace">Cⁱʲ = aⁱbʲ  (no sum)</text>
        <text x={p(0)+18} y={250} fontSize="11" fill="#1F3864" fontWeight="700">Components: n^(r+s)</text>
        <text x={p(0)+18} y={266} fontSize="11" fill="#444">Generalises outer product</text>
        <text x={p(0)+18} y={282} fontSize="11" fill="#444">of two matrices to any rank</text>

        {/* Notation row */}
        <rect x={p(0)+10} y={290} width={PW-20} height={76} rx={3} fill="#fff7ee" stroke="#e07b00" strokeWidth="1"/>
        <text x={p(0)+18} y={306} fontSize="11" fill="#1F3864" fontWeight="700">Mixed rank example:</text>
        <text x={p(0)+18} y={322} fontSize="11" fill="#444" fontFamily="'Courier New',monospace">Tⁱʲₖ = AⁱʲBₖ</text>
        <text x={p(0)+18} y={338} fontSize="11" fill="#555">Rank 2 ⊗ Rank 1 = Rank 3</text>
        <text x={p(0)+18} y={354} fontSize="11" fill="#555">Result has 3 indices: i, j, k</text>

        {/* ── Panel 2: Contraction ── */}
        <rect x={p(1)} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={p(1)+PW/2} y={28} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">Contraction</text>
        <text x={p(1)+PW/2} y={43} textAnchor="middle" fontSize="12" fill="#27ae60" fontFamily="'Courier New',monospace" fontWeight="700">Bⁱ = Tⁱʲⱼ  (sum on j)</text>

        {/* 3x3 matrix being contracted */}
        {[0,1,2].map(row => [0,1,2].map(col => {
          const onDiag = row === col;
          return (
            <g key={`${row}-${col}`}>
              <rect x={p(1)+30+col*44} y={60+row*44} width={40} height={38} rx={2}
                fill={onDiag ? '#f0fff4' : '#fafafa'}
                stroke={onDiag ? '#27ae60' : '#ccd9ea'}
                strokeWidth={onDiag ? 2 : 0.8}/>
              <text x={p(1)+50+col*44} y={83+row*44} textAnchor="middle"
                fontSize="12" fill={onDiag ? '#27ae60' : '#bbb'}
                fontFamily="'Courier New',monospace" fontWeight={onDiag ? '700':'400'}>
                T{['¹','²','³'][row]}{['¹','²','³'][col]}
              </text>
            </g>
          );
        }))}

        {/* Diagonal highlight label */}
        <text x={p(1)+PW/2} y={202} textAnchor="middle" fontSize="11" fill="#27ae60" fontWeight="700">Diagonal elements summed</text>
        <text x={p(1)+PW/2} y={216} textAnchor="middle" fontSize="11" fill="#555" fontFamily="'Courier New',monospace">= T¹₁ + T²₂ + T³₃</text>

        {/* Trace arrow */}
        {[0,1,2].map(k => (
          <line key={k} x1={p(1)+30+k*44+40} y1={60+k*44+19}
            x2={p(1)+PW-14} y2={200-(50-k*10)}
            stroke="#27ae60" strokeWidth="1" strokeDasharray="3,2" strokeOpacity="0.5"/>
        ))}

        {/* Steps */}
        <rect x={p(1)+10} y={230} width={PW-20} height={60} rx={3} fill="#f0fff4" stroke="#27ae60" strokeWidth="1"/>
        <text x={p(1)+18} y={246} fontSize="11" fill="#1F3864" fontWeight="700">Contraction reduces rank by 2:</text>
        <text x={p(1)+18} y={262} fontSize="11" fill="#444" fontFamily="'Courier New',monospace">rank-2 → scalar (trace)</text>
        <text x={p(1)+18} y={278} fontSize="11" fill="#444" fontFamily="'Courier New',monospace">rank-3 → rank-1 (vector)</text>

        <rect x={p(1)+10} y={298} width={PW-20} height={68} rx={3} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1"/>
        <text x={p(1)+18} y={314} fontSize="11" fill="#1F3864" fontWeight="700">Full contraction = inner product:</text>
        <text x={p(1)+18} y={330} fontSize="11" fill="#444" fontFamily="'Courier New',monospace">AⁱʲBᵢⱼ = Σᵢ Σⱼ AⁱʲBᵢⱼ</text>
        <text x={p(1)+18} y={346} fontSize="11" fill="#555">Frobenius inner product</text>
        <text x={p(1)+18} y={360} fontSize="11" fill="#555">of two matrices</text>

        {/* ── Panel 3: Symmetry / antisymmetry ── */}
        <rect x={p(2)} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={p(2)+PW/2} y={28} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">Symmetry Properties</text>

        {/* Symmetric */}
        <rect x={p(2)+10} y={36} width={PW-20} height={92} rx={3} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1.2"/>
        <text x={p(2)+18} y={52} fontSize="12" fill="#2E75B6" fontWeight="700">Symmetric:  Tᵢⱼ = Tⱼᵢ</text>
        {[0,1,2].map(row => [0,1,2].map(col => {
          const val = row===col ? '•' : (row<col ? '+' : '+');
          const clr = row===col ? '#1F3864' : '#2E75B6';
          return (
            <g key={`s${row}${col}`}>
              <rect x={p(2)+18+col*30} y={58+row*25} width={27} height={21} rx={2}
                fill={row===col?'#ddeeff':'#EBF3FB'} stroke="#2E75B6" strokeWidth="0.8"/>
              <text x={p(2)+31+col*30} y={73+row*25} textAnchor="middle"
                fontSize="12" fill={clr} fontWeight="700">{val}</text>
            </g>
          );
        }))}
        <text x={p(2)+PW/2+10} y={70} fontSize="11" fill="#555">Mirror across</text>
        <text x={p(2)+PW/2+10} y={84} fontSize="11" fill="#555">the diagonal.</text>
        <text x={p(2)+PW/2+10} y={100} fontSize="11" fill="#555">n(n+1)/2</text>
        <text x={p(2)+PW/2+10} y={114} fontSize="11" fill="#555">independent.</text>

        {/* Antisymmetric */}
        <rect x={p(2)+10} y={136} width={PW-20} height={92} rx={3} fill="#fff0f0" stroke="#c0392b" strokeWidth="1.2"/>
        <text x={p(2)+18} y={152} fontSize="12" fill="#c0392b" fontWeight="700">Antisymmetric:  Tᵢⱼ = -Tⱼᵢ</text>
        {[0,1,2].map(row => [0,1,2].map(col => {
          const val = row===col ? '0' : (row<col ? '+' : '−');
          const clr = row===col ? '#888' : (row<col ? '#c0392b' : '#c0392b');
          return (
            <g key={`a${row}${col}`}>
              <rect x={p(2)+18+col*30} y={158+row*25} width={27} height={21} rx={2}
                fill={row===col?'#eee':'#fff0f0'} stroke="#c0392b" strokeWidth="0.8"/>
              <text x={p(2)+31+col*30} y={173+row*25} textAnchor="middle"
                fontSize="12" fill={clr} fontWeight="700">{val}</text>
            </g>
          );
        }))}
        <text x={p(2)+PW/2+10} y={170} fontSize="11" fill="#555">Diagonal = 0.</text>
        <text x={p(2)+PW/2+10} y={184} fontSize="11" fill="#555">n(n−1)/2</text>
        <text x={p(2)+PW/2+10} y={198} fontSize="11" fill="#555">independent.</text>
        <text x={p(2)+PW/2+10} y={214} fontSize="11" fill="#555">Cross-diagonal</text>
        <text x={p(2)+PW/2+10} y={228} fontSize="11" fill="#555">negates.</text>

        {/* Decomposition */}
        <rect x={p(2)+10} y={236} width={PW-20} height={58} rx={3} fill="#fff7ee" stroke="#e07b00" strokeWidth="1"/>
        <text x={p(2)+18} y={252} fontSize="11" fill="#e07b00" fontWeight="700">Any rank-2 tensor decomposes:</text>
        <text x={p(2)+18} y={268} fontSize="11" fill="#444" fontFamily="'Courier New',monospace">Tᵢⱼ = T(ᵢⱼ) + T[ᵢⱼ]</text>
        <text x={p(2)+18} y={284} fontSize="11" fill="#555">symmetric + antisymmetric part</text>

        {/* Levi-Civita */}
        <rect x={p(2)+10} y={302} width={PW-20} height={64} rx={3} fill="#f5d0ff" stroke="#9b59b6" strokeWidth="1"/>
        <text x={p(2)+18} y={318} fontSize="11" fill="#9b59b6" fontWeight="700">Levi-Civita symbol εᵢⱼₖ</text>
        <text x={p(2)+18} y={334} fontSize="11" fill="#444">+1 if (i,j,k) is even perm of (1,2,3)</text>
        <text x={p(2)+18} y={350} fontSize="11" fill="#444">−1 if odd permutation;  0 if repeated</text>
        <text x={p(2)+18} y={362} fontSize="10" fill="#9b59b6">Rank-3 totally antisymmetric tensor</text>

      </svg>
      <div style={S.dCaption}>The outer product increases tensor rank by combining indices; contraction decreases rank by summing over a repeated index pair. Symmetric tensors have fewer independent components [n(n+1)/2]; antisymmetric tensors have diagonal zeros [n(n-1)/2]. Any rank-2 tensor splits uniquely into its symmetric and antisymmetric parts.</div>
    </div>
  );
}

// ── Diagram 5: Change of Basis & Transformation Laws ────────────
function DiagramTransformation() {
  const W = 900, H = 350;
  const LP = 14, LW = 380;
  const RP = LP + LW + 18, RW = W - RP - 14;

  // Left: two coordinate systems rotated
  const ox = LP + LW/2, oy = 200, axLen = 110;
  const theta = 35 * Math.PI / 180;

  // Original axes (black)
  const e1x = [axLen, 0], e1y = [0, -axLen];
  // Rotated axes (blue)
  const r1x = [axLen*Math.cos(theta), -axLen*Math.sin(theta)];
  const r1y = [axLen*Math.sin(theta),  axLen*Math.cos(theta)];

  // A vector in the original frame
  const vx = 70, vy = -55;
  // Its components in rotated frame (just drawn)
  const vxR = vx*Math.cos(theta) - vy*Math.sin(theta); // not used for drawing but conceptual

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Change of Basis — How Tensor Components Transform</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* Left panel */}
        <rect x={LP} y={10} width={LW} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={LP+LW/2} y={28} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">Two Frames: Original and Rotated</text>

        {/* Original axes */}
        <line x1={ox} y1={oy} x2={ox+e1x[0]} y2={oy+e1x[1]} stroke="#888" strokeWidth="1.8"/>
        <polygon points={`${ox+e1x[0]},${oy+e1x[1]} ${ox+e1x[0]-8},${oy+e1x[1]-3} ${ox+e1x[0]-8},${oy+e1x[1]+3}`} fill="#888"/>
        <text x={ox+e1x[0]+6} y={oy+e1x[1]+4} fontSize="12" fill="#888">x</text>

        <line x1={ox} y1={oy} x2={ox+e1y[0]} y2={oy+e1y[1]} stroke="#888" strokeWidth="1.8"/>
        <polygon points={`${ox+e1y[0]},${oy+e1y[1]} ${ox+e1y[0]-3},${oy+e1y[1]+8} ${ox+e1y[0]+3},${oy+e1y[1]+8}`} fill="#888"/>
        <text x={ox+e1y[0]+5} y={oy+e1y[1]-4} fontSize="12" fill="#888">y</text>

        {/* Rotated axes */}
        <line x1={ox} y1={oy} x2={ox+r1x[0]} y2={oy+r1x[1]} stroke="#2E75B6" strokeWidth="2.2"/>
        <polygon points={`${ox+r1x[0]},${oy+r1x[1]} ${ox+r1x[0]-7},${oy+r1x[1]+5} ${ox+r1x[0]-4},${oy+r1x[1]-7}`} fill="#2E75B6"/>
        <text x={ox+r1x[0]+6} y={oy+r1x[1]+4} fontSize="12" fill="#2E75B6" fontWeight="700">x'</text>

        <line x1={ox} y1={oy} x2={ox+r1y[0]} y2={oy+r1y[1]} stroke="#2E75B6" strokeWidth="2.2"/>
        <polygon points={`${ox+r1y[0]},${oy+r1y[1]} ${ox+r1y[0]+6},${oy+r1y[1]+6} ${ox+r1y[0]-6},${oy+r1y[1]+6}`} fill="#2E75B6"/>
        <text x={ox+r1y[0]-20} y={oy+r1y[1]-8} fontSize="12" fill="#2E75B6" fontWeight="700">y'</text>

        {/* Angle arc */}
        <path d={`M ${ox+45},${oy} A 45 45 0 0 0 ${ox+45*Math.cos(theta)},${oy-45*Math.sin(theta)}`}
          fill="none" stroke="#e07b00" strokeWidth="1.5"/>
        <text x={ox+50} y={oy-16} fontSize="12" fill="#e07b00" fontWeight="700">θ</text>

        {/* Vector v */}
        <line x1={ox} y1={oy} x2={ox+vx} y2={oy+vy} stroke="#c0392b" strokeWidth="2.5"/>
        <polygon points={`${ox+vx},${oy+vy} ${ox+vx-6},${oy+vy+7} ${ox+vx-7},${oy+vy-5}`} fill="#c0392b"/>
        <text x={ox+vx+6} y={oy+vy-4} fontSize="13" fill="#c0392b" fontWeight="700">v</text>

        {/* Component projections onto original */}
        <line x1={ox+vx} y1={oy+vy} x2={ox+vx} y2={oy} stroke="#888" strokeWidth="1" strokeDasharray="4,3"/>
        <line x1={ox+vx} y1={oy+vy} x2={ox}    y2={oy+vy} stroke="#888" strokeWidth="1" strokeDasharray="4,3"/>
        <text x={ox+vx/2} y={oy+14} textAnchor="middle" fontSize="11" fill="#888">vₓ</text>
        <text x={ox-16}   y={oy+vy/2} fontSize="11" fill="#888">vy</text>

        {/* Formula box */}
        <rect x={LP+10} y={H-100} width={LW-20} height={86} rx={3} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1"/>
        <text x={LP+20} y={H-84} fontSize="11" fill="#1F3864" fontWeight="700">Rotation matrix R (change of basis):</text>
        <text x={LP+20} y={H-68} fontSize="11" fill="#2E75B6" fontFamily="'Courier New',monospace">v'ⁱ = Rⁱⱼ vʲ  (contravariant)</text>
        <text x={LP+20} y={H-52} fontSize="11" fill="#27ae60" fontFamily="'Courier New',monospace">w'ᵢ = (R⁻¹)ʲᵢ wⱼ  (covariant)</text>
        <text x={LP+20} y={H-36} fontSize="11" fill="#555">For orthogonal R: R⁻¹ = Rᵀ, so co/contra are same</text>
        <text x={LP+20} y={H-20} fontSize="11" fill="#555">Tensor Tⁱʲ transforms with R on each upper index</text>

        {/* Right panel: transformation laws */}
        <rect x={RP} y={10} width={RW} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={RP+RW/2} y={28} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">Transformation Laws</text>

        {[
          {
            rank:'Scalar (rank 0)', color:'#2E75B6', bg:'#EBF3FB', bc:'#2E75B6',
            law:"a' = a",
            note:'Invariant — no transformation needed',
            y: 36,
          },
          {
            rank:'Contravariant vector (rank 1)', color:'#27ae60', bg:'#f0fff4', bc:'#27ae60',
            law:"v'ⁱ = (∂x'ⁱ/∂xʲ) vʲ",
            note:'Transforms like basis changes (inverse)',
            y: 96,
          },
          {
            rank:'Covariant vector (rank 1)', color:'#e07b00', bg:'#fff7ee', bc:'#e07b00',
            law:"w'ᵢ = (∂xʲ/∂x'ⁱ) wⱼ",
            note:'Transforms like the coordinates themselves',
            y: 156,
          },
          {
            rank:'Mixed rank-2 tensor Tⁱⱼ', color:'#9b59b6', bg:'#f5d0ff', bc:'#9b59b6',
            law:"T'ⁱⱼ = (∂x'ⁱ/∂xᵃ)(∂xᵇ/∂x'ʲ) Tᵃᵦ",
            note:'One Jacobian factor per index',
            y: 216,
          },
          {
            rank:'Tensor identity', color:'#c0392b', bg:'#fff0f0', bc:'#c0392b',
            law:"If Tⁱʲ = 0 in one frame → 0 in all",
            note:'Tensors encode frame-independent facts',
            y: 276,
          },
        ].map(({ rank, color, bg, bc, law, note, y }) => (
          <g key={y}>
            <rect x={RP+10} y={y} width={RW-20} height={52} rx={3} fill={bg} stroke={bc} strokeWidth="1"/>
            <text x={RP+18} y={y+15} fontSize="11" fill={color} fontWeight="700">{rank}</text>
            <text x={RP+18} y={y+31} fontSize="11" fill="#333" fontFamily="'Courier New',monospace">{law}</text>
            <text x={RP+18} y={y+46} fontSize="10" fill="#555">{note}</text>
          </g>
        ))}

      </svg>
      <div style={S.dCaption}>The defining property of a tensor is precisely how it transforms under a change of coordinates: each upper index picks up a Jacobian factor (∂x'/∂x) and each lower index picks up an inverse Jacobian (∂x/∂x'). A quantity that transforms this way for all coordinate changes is, by definition, a tensor.</div>
    </div>
  );
}

// ── Diagram 6: Special Tensors ───────────────────────────────────
function DiagramSpecialTensors() {
  const W = 900, H = 320;
  const cols = 3;
  const PW = (W - 14 - (cols-1)*14 - 14) / cols;
  const p = i => 14 + i*(PW+14);

  const specials = [
    {
      name: 'Kronecker Delta δⁱⱼ',
      color: '#2E75B6', bg: '#EBF3FB', bc: '#2E75B6',
      desc: 'The identity tensor.\nδⁱⱼ = 1 if i=j, else 0.\nδⁱⱼ vʲ = vⁱ  (index substitution)\nTrace: δⁱᵢ = n (dimension)',
      grid: (cx, cy) => {
        const elems = [];
        for(let r=0;r<3;r++) for(let c=0;c<3;c++){
          elems.push(
            <g key={`${r}${c}`}>
              <rect x={cx-33+c*23} y={cy-28+r*23} width={20} height={20} rx={2}
                fill={r===c?'#2E75B6':'#EBF3FB'}
                stroke="#2E75B6" strokeWidth="0.8"/>
              <text x={cx-23+c*23} y={cy-14+r*23} textAnchor="middle"
                fontSize="11" fill={r===c?'#fff':'#2E75B6'} fontWeight="700">
                {r===c?'1':'0'}
              </text>
            </g>
          );
        }
        return elems;
      },
    },
    {
      name: 'Levi-Civita εᵢⱼₖ',
      color: '#9b59b6', bg: '#f5d0ff', bc: '#9b59b6',
      desc: 'Totally antisymmetric rank-3.\nε₁₂₃=+1, ε₂₁₃=−1, else 0.\nεᵢⱼₖ AⁱBʲCᵏ = det[A,B,C]\nEncodes cross products & volumes',
      grid: (cx, cy) => {
        const perms = [[1,2,3,'+1'],[2,3,1,'+1'],[3,1,2,'+1'],[2,1,3,'−1'],[1,3,2,'−1'],[3,2,1,'−1']];
        return perms.map(([i,j,k,v], idx) => (
          <g key={idx}>
            <rect x={cx-58} y={cy-60+idx*20} width={116} height={17} rx={2}
              fill={v==='+1'?'#f5d0ff':'#fff0f0'} stroke="#9b59b6" strokeWidth="0.6"/>
            <text x={cx-48} y={cy-47+idx*20} fontSize="10" fill="#9b59b6" fontFamily="'Courier New',monospace">
              ε{i}{j}{k} = {v}
            </text>
          </g>
        ));
      },
    },
    {
      name: 'Metric Tensor gᵢⱼ',
      color: '#e07b00', bg: '#fff7ee', bc: '#e07b00',
      desc: 'Symmetric rank-2 tensor.\ngᵢⱼ = gⱼᵢ (always symmetric).\nds² = gᵢⱼ dxⁱ dxʲ (line element)\ngᵢⱼ gʲᵏ = δᵢᵏ  (inverse relation)',
      grid: (cx, cy) => {
        const cells = [['g₁₁','g₁₂','g₁₃'],['g₂₁','g₂₂','g₂₃'],['g₃₁','g₃₂','g₃₃']];
        return cells.map((row, r) => row.map((cell, c) => (
          <g key={`${r}${c}`}>
            <rect x={cx-42+c*30} y={cy-34+r*25} width={28} height={21} rx={2}
              fill={r<=c?'#fff7ee':'#fff'} stroke="#e07b00" strokeWidth={r===c?1.5:0.8}/>
            <text x={cx-28+c*30} y={cy-19+r*25} textAnchor="middle"
              fontSize="9" fill={r===c?'#e07b00':'#aaa'} fontFamily="'Courier New',monospace">{cell}</text>
          </g>
        )));
      },
    },
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Special Tensors — Kronecker Delta, Levi-Civita Symbol, and Metric Tensor</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {specials.map(({ name, color, bg, bc, desc, grid }, i) => {
          const cx = p(i) + PW/2;
          const gridCY = 115;
          return (
            <g key={i}>
              <rect x={p(i)} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
              {/* Header */}
              <rect x={p(i)} y={10} width={PW} height={30} rx={5} fill={color}/>
              <rect x={p(i)} y={30} width={PW} height={10} fill={color}/>
              <text x={cx} y={29} textAnchor="middle" fontSize="12" fill="#fff" fontWeight="700">{name}</text>

              {/* Grid visual */}
              {grid(cx, gridCY)}

              {/* Description */}
              <rect x={p(i)+10} y={gridCY+50} width={PW-20} height={H-20-gridCY-60} rx={3} fill={bg} stroke={bc} strokeWidth="1"/>
              {desc.split('\n').map((line, j) => (
                <text key={j} x={p(i)+18} y={gridCY+68+j*22} fontSize="11" fill="#333">{line}</text>
              ))}
            </g>
          );
        })}

      </svg>
      <div style={S.dCaption}>The Kronecker delta acts as the identity for index substitution. The Levi-Civita symbol is the unique totally antisymmetric rank-3 tensor (up to scaling) and encodes orientation, cross products, and determinants. Together, gᵢⱼ, δⁱⱼ, and εᵢⱼₖ are the fundamental building blocks of tensor algebra in any dimension.</div>
    </div>
  );
}

// ── Main ─────────────────────────────────────────────────────────
export default function TensorMath() {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Tensor Mathematics in Linear Algebra</h1>
      <p style={S.p}>Tensors are the natural generalisation of scalars, vectors, and matrices to objects with any number of indices. A tensor of rank r in n dimensions has n^r components, each carrying r index labels, and transforms under coordinate changes according to a precise rule — that transformation law is the defining property of a tensor. Tensors are the language of modern physics (general relativity, continuum mechanics, electromagnetism) and machine learning (data arrays, weight tensors), because they encode geometric and physical facts that are independent of any particular coordinate choice.</p>

      <InfoBox title="Core Idea: A tensor is defined by how it transforms, not just by its array of numbers" items={[
        "Rank 0 (scalar): 1 component, no indices — invariant under all coordinate changes",
        "Rank 1 (vector): n components, 1 index — contravariant (up) or covariant (down)",
        "Rank 2 (matrix/tensor): n² components, 2 indices — generalises linear maps and bilinear forms",
        "Einstein summation: a repeated index (one up, one down) implies a sum — no Σ sign needed",
        "Metric tensor gᵢⱼ: converts between covariant and contravariant components; encodes geometry",
        "Tensor equation: if Tⁱʲ = Sⁱʲ in one frame, it holds in every frame — tensors are coordinate-free",
      ]}/>

      <h2 style={S.h2}>Tensor Rank and the Rank Hierarchy</h2>
      <DiagramRankHierarchy/>
      <Table2 rows={[
        ["Rank (order)",         "Number of indices r; total components = nʳ in n dimensions\nRank is intrinsic — independent of basis"],
        ["Rank 0: scalar",       "Single number; no indices\nExamples: mass, temperature, energy"],
        ["Rank 1: vector",       "n components, one index: vⁱ or vᵢ\nExamples: velocity, force, gradient"],
        ["Rank 2: 2-tensor",     "n² components, two indices: Tⁱʲ, Tᵢⱼ, or Tⁱⱼ\nExamples: stress tensor, inertia tensor, metric"],
        ["Rank 3+",              "n³, n⁴, … components\nExamples: piezoelectric (3), Riemann curvature (4)"],
        ["Type (p,q) tensor",    "p contravariant (upper) indices, q covariant (lower) indices\nTotal rank = p+q; nᵖ⁺ q components"],
      ]}/>

      <h2 style={S.h2}>Index Notation and Einstein Summation</h2>
      <DiagramEinstein/>
      <Table2 rows={[
        ["Free index",           "Appears exactly once per term — represents one component label\nMust appear on both sides of an equation with the same position"],
        ["Dummy (contracted) index","Appears exactly twice (once up, once down) → implies summation\nCan be renamed freely: aⁱbᵢ = aʲbⱼ"],
        ["Dot product",          "c = aⁱbᵢ = Σᵢ aⁱbᵢ\nContraction of a contravariant and covariant vector"],
        ["Matrix multiplication","Cⁱⱼ = AⁱₖBᵏⱼ  (k is the dummy index summed over)\nEinstein notation makes the contraction explicit"],
        ["Trace",                "tr(T) = Tⁱᵢ = T¹₁ + T²₂ + … + Tⁿₙ\nContraction of both indices of a rank-2 tensor"],
        ["Outer product",        "Cⁱʲₖ = AⁱʲBₖ — no repeated index, rank increases\nNo summation; each index labels an independent dimension"],
      ]}/>

      <h2 style={S.h2}>Covariant vs Contravariant Components and the Metric</h2>
      <DiagramMetric/>
      <Table2 rows={[
        ["Contravariant vⁱ",     "Components measured along the basis grid lines\nTransform inversely to the basis: v'ⁱ = (∂x'ⁱ/∂xʲ) vʲ"],
        ["Covariant vᵢ",         "Components measured by perpendicular projection\nTransform like the basis: v'ᵢ = (∂xʲ/∂x'ⁱ) vⱼ"],
        ["Metric tensor gᵢⱼ",    "gᵢⱼ = eᵢ · eⱼ — inner products of basis vectors\nAlways symmetric: gᵢⱼ = gⱼᵢ"],
        ["Lowering an index",    "vᵢ = gᵢⱼ vʲ  (sum over j)\nTurns a contravariant vector into a covariant one"],
        ["Raising an index",     "vⁱ = gⁱʲ vⱼ  where gⁱʲ = (g⁻¹)ⁱʲ\nInverse metric raises indices"],
        ["Length and angle",     "|v|² = gᵢⱼ vⁱ vʲ;  v·w = gᵢⱼ vⁱ wʲ\nIn Cartesian frame: gᵢⱼ = δᵢⱼ, so vⁱ = vᵢ"],
        ["Euclidean vs curved",  "Flat space: gᵢⱼ = diag(1,1,…,1)  (Cartesian)\nCurved space: gᵢⱼ varies with position (Riemannian geometry)"],
      ]}/>

      <h2 style={S.h2}>Core Tensor Operations</h2>
      <DiagramOperations/>
      <Table2 rows={[
        ["Outer product ⊗",      "Cⁱʲ = aⁱ ⊗ bʲ = aⁱbʲ  (no sum)\nRank adds: (p,q) ⊗ (r,s) = (p+r, q+s) tensor"],
        ["Contraction",          "Set one upper and one lower index equal and sum\nReduces rank by 2: Bⁱ = Tⁱʲⱼ"],
        ["Inner product",        "Full double contraction: AⁱʲBᵢⱼ = Σᵢ Σⱼ AⁱʲBᵢⱼ\nFrobenius inner product of two rank-2 tensors"],
        ["Symmetrisation",       "T(ᵢⱼ) = (Tᵢⱼ + Tⱼᵢ)/2  (symmetric part)\nn(n+1)/2 independent components in n dimensions"],
        ["Antisymmetrisation",   "T[ᵢⱼ] = (Tᵢⱼ − Tⱼᵢ)/2  (antisymmetric part)\nn(n−1)/2 independent components; diagonal = 0"],
        ["Tensor decomposition", "Tᵢⱼ = T(ᵢⱼ) + T[ᵢⱼ]  (unique for rank-2)\nHigher-rank decompositions use Young tableaux"],
        ["Trace-free part",      "T̂ᵢⱼ = Tᵢⱼ − (1/n)gᵢⱼTᵏₖ  (STF decomposition)\nUsed in multipole expansions and relativity"],
      ]}/>

      <h2 style={S.h2}>Change of Basis and Transformation Laws</h2>
      <DiagramTransformation/>
      <Table2 rows={[
        ["Jacobian matrix",      "Jⁱⱼ = ∂x'ⁱ/∂xʲ  (partial derivatives of new coords)\nInverse Jacobian: (J⁻¹)ⁱⱼ = ∂xⁱ/∂x'ʲ"],
        ["Contravariant rule",   "v'ⁱ = Jⁱⱼ vʲ  (multiply by Jacobian per upper index)\nVectors, displacements, velocities"],
        ["Covariant rule",       "w'ᵢ = (J⁻¹)ʲᵢ wⱼ  (multiply by inverse Jacobian per lower index)\nGradients, differential forms, co-vectors"],
        ["Rank-2 tensor",        "T'ⁱⱼ = Jⁱₐ (J⁻¹)ᵇⱼ Tᵃᵦ\nOne factor per index, upper/lower matched"],
        ["Orthogonal transforms","For rotation R: R⁻¹ = Rᵀ so upper = lower transforms\nCartesian tensors: no distinction between co/contra"],
        ["Physical invariance",  "If Tⁱʲ... = 0 in any one frame → = 0 in all frames\nConservation laws, field equations are tensor equations"],
      ]}/>

      <h2 style={S.h2}>Special Tensors</h2>
      <DiagramSpecialTensors/>
      <Table2 rows={[
        ["Kronecker delta δⁱⱼ",  "δⁱⱼ = 1 if i=j, else 0  (identity matrix as tensor)\nδⁱⱼ vʲ = vⁱ  (substitution property)"],
        ["Levi-Civita εᵢⱼₖ",    "Totally antisymmetric: +1 (even perm), −1 (odd), 0 (repeat)\nεᵢⱼₖ aⁱ bʲ cᵏ = scalar triple product = det[a,b,c]"],
        ["Cross product",        "cᵢ = εᵢⱼₖ aʲ bᵏ  (a × b using Levi-Civita)\nWorks in 3D; Levi-Civita is specifically 3D here"],
        ["Metric tensor gᵢⱼ",   "Symmetric, encodes inner product structure\ngᵢⱼ gʲᵏ = δᵢᵏ  (metric and its inverse are reciprocal)"],
        ["Riemann tensor Rⁱⱼₖₗ","Rank-4 tensor measuring curvature of space\nR=0 everywhere ↔ space is flat (Euclidean/Minkowski)"],
        ["Cauchy stress tensor", "σⁱʲ: symmetric rank-2; σⁱʲ nⱼ = traction vector\nFundamental object in continuum mechanics"],
      ]}/>

      <InfoBox title="Key Takeaway — Tensor Checklist" items={[
        "What rank? → Count the indices (or n^r component slots needed)",
        "Summation or free index? → Two identical indices (one up, one down) = sum; one occurrence = free",
        "Need to raise/lower? → Use gᵢⱼ to lower and gⁱʲ (inverse metric) to raise",
        "Is it a tensor? → Check: does it transform with the right number of Jacobian factors?",
        "Symmetric or antisymmetric? → Tᵢⱼ = Tⱼᵢ (sym) or Tᵢⱼ = -Tⱼᵢ (antisym); any rank-2 is a sum of both",
        "Cross product in index notation → cᵢ = εᵢⱼₖ aʲ bᵏ using the Levi-Civita symbol",
        "Trace / contraction? → Set one upper = one lower index; rank drops by 2",
        "Physical law is a tensor equation → Automatically valid in all coordinate systems",
      ]}/>
    </div>
  );
}